import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw } from "lucide-react";
import { statusMeta, priorityMeta, LOCATION_TYPES, fmtQty } from "@/pages/tasks/taskConstants";
import TaskDetail from "@/pages/tasks/TaskDetail";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle } from "@/components/ops-ui";

export default function TaskAdmin() {
  const { can } = useAuth();
  const canManage = can("task:manage");
  const canTypes = can("task:type_manage");
  const canLoc = can("location:manage");
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState("tasks");
  const [openId, setOpenId] = useState(sp.get("task") || null);

  useEffect(() => { if (sp.get("task")) { setOpenId(sp.get("task")); } }, [sp]);

  return (
    <div data-testid="taskadmin-page" className="max-w-6xl mx-auto">
      <header className="mb-4">
        <div className="caption text-xs uppercase tracking-widest text-slate-400">Operations</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-0.5">Task Management</h1>
      </header>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="tasks" data-testid="tab-tasks">All Tasks</TabsTrigger>
          {canManage && <TabsTrigger value="create" data-testid="tab-create">Create</TabsTrigger>}
          <TabsTrigger value="types" data-testid="tab-types">Task Types</TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">Warehouse Locations</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Issues &amp; Discrepancies</TabsTrigger>
        </TabsList>
        <TabsContent value="tasks"><AllTasks onOpen={setOpenId} /></TabsContent>
        {canManage && <TabsContent value="create"><CreateTasks /></TabsContent>}
        <TabsContent value="types"><TaskTypes canManage={canTypes} /></TabsContent>
        <TabsContent value="locations"><Locations canManage={canLoc} /></TabsContent>
        <TabsContent value="issues"><IssuesPanel /></TabsContent>
      </Tabs>
      <TaskDetail taskId={openId} open={!!openId} onClose={() => { setOpenId(null); setSp({}); }} onChanged={() => {}} mode="admin" canManage={canManage} />
    </div>
  );
}

function AllTasks({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [filt, setFilt] = useState({ status: "", task_type: "", q: "" });
  const { density, setDensity } = useUiPrefs("task-admin");
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries(filt).filter(([, v]) => v));
    api.get("/tasks", { params: p }).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [filt]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-all-tasks">
      <div className="flex flex-wrap gap-2 mb-3">
        <Input placeholder="Search Task ID / SKU / WH" value={filt.q} onChange={(e) => setFilt((f) => ({ ...f, q: e.target.value }))} className="h-9 w-56" data-testid="task-search" />
        <Select value={filt.status || "all"} onValueChange={(v) => setFilt((f) => ({ ...f, status: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-40 h-9" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>{["all", "to_do", "taken", "in_progress", "paused", "blocked", "ready_to_resume", "partially_completed", "done", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : statusMeta(s).label}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} data-testid="task-refresh"><RefreshCw className="w-4 h-4" /></Button>
        <div className="ml-auto"><DensityToggle density={density} setDensity={setDensity} testid="task-density-toggle" /></div>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`w-full text-sm ${density === "compact" ? "[&_td]:py-1 [&_th]:py-1" : ""}`} data-testid="admin-tasks-table">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="text-left p-2">Task</th><th className="text-left p-2">Type</th><th className="text-left p-2">Assigned</th><th className="text-left p-2">Priority</th><th className="text-left p-2">Status</th><th className="text-left p-2">Qty</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-slate-400">No tasks</td></tr>
              : rows.map((t) => (
                <tr key={t.id} className="border-t hover:bg-slate-50" data-testid={`admin-task-row-${t.id}`}>
                  <td className="p-2 font-mono text-xs">{t.task_no}</td>
                  <td className="p-2">{t.task_type_name}</td>
                  <td className="p-2 text-xs">{t.assigned_employee || t.assigned_team || "Pool"}</td>
                  <td className="p-2"><Badge variant="outline" className={priorityMeta(t.priority).cls}>{priorityMeta(t.priority).label}</Badge></td>
                  <td className="p-2"><Badge variant="outline" className={statusMeta(t.status).cls}>{statusMeta(t.status).label}</Badge></td>
                  <td className="p-2 text-xs">{fmtQty(t.completed_qty)}/{fmtQty(t.expected_qty)}</td>
                  <td className="p-2 text-right"><Button size="sm" variant="outline" data-testid={`admin-open-${t.id}`} onClick={() => onOpen(t.id)}>Open</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateTasks() {
  const [mode, setMode] = useState("pool");
  return (
    <div data-testid="admin-create">
      <div className="flex gap-2 mb-3">
        <Button size="sm" variant={mode === "pool" ? "default" : "outline"} onClick={() => setMode("pool")} data-testid="create-mode-pool">Work Pool (multi-SKU)</Button>
        <Button size="sm" variant={mode === "single" ? "default" : "outline"} onClick={() => setMode("single")} data-testid="create-mode-single">Single / Manual Task</Button>
      </div>
      {mode === "pool" ? <CreatePool /> : <CreateSingle />}
    </div>
  );
}

function CreatePool() {
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({ task_type_code: "", warehouse_code: "", priority: "normal" });
  const [lines, setLines] = useState([{ sku: "", expected_qty: "", packing_requirement: "" }]);
  useEffect(() => { api.get("/task-types", { params: { active_only: true } }).then((r) => setTypes((r.data || []).filter((t) => t.allows_multi_sku))).catch(() => {}); }, []);
  const prefill = async () => {
    if (!f.warehouse_code) return toast.error("Enter a warehouse code first");
    try { const r = await api.get("/tasks/arrival-skus", { params: { warehouse_code: f.warehouse_code } });
      if (!r.data.length) return toast.message("No arrival SKUs found for that code");
      setLines(r.data.map((x) => ({ sku: x.sku, expected_qty: x.expected_qty, packing_requirement: "" })));
      toast.success(`Prefilled ${r.data.length} SKU(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submit = async () => {
    const payload = { ...f, lines: lines.filter((l) => l.sku).map((l) => ({ sku: l.sku, expected_qty: l.expected_qty === "" ? null : Number(l.expected_qty), packing_requirement: l.packing_requirement })) };
    try { const r = await api.post("/tasks/pools", payload); toast.success(`Pool created · ${r.data.lines_created} SKU lines`); setLines([{ sku: "", expected_qty: "", packing_requirement: "" }]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 max-w-2xl" data-testid="create-pool-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">Task Type</Label>
          <Select value={f.task_type_code} onValueChange={(v) => setF((p) => ({ ...p, task_type_code: v }))}><SelectTrigger data-testid="pool-type"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{types.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs">Warehouse Code</Label><Input data-testid="pool-wh" value={f.warehouse_code} onChange={(e) => setF((p) => ({ ...p, warehouse_code: e.target.value }))} /></div>
        <div><Label className="text-xs">Priority</Label>
          <Select value={f.priority} onValueChange={(v) => setF((p) => ({ ...p, priority: v }))}><SelectTrigger data-testid="pool-priority"><SelectValue /></SelectTrigger><SelectContent>{["normal", "high", "urgent", "order_blocking"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={prefill} data-testid="pool-prefill">Prefill SKUs from Inbound/Arrival</Button>
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="flex gap-2 items-end" data-testid={`pool-line-input-${i}`}>
            <div className="flex-1"><Label className="text-xs">SKU</Label><Input value={l.sku} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, sku: e.target.value } : x))} /></div>
            <div className="w-28"><Label className="text-xs">Expected Qty</Label><Input type="number" value={l.expected_qty} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, expected_qty: e.target.value } : x))} /></div>
            <div className="flex-1"><Label className="text-xs">Packing</Label><Input value={l.packing_requirement} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, packing_requirement: e.target.value } : x))} placeholder="e.g. OPP Bag" /></div>
            <Button size="icon" variant="ghost" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => setLines((ls) => [...ls, { sku: "", expected_qty: "", packing_requirement: "" }])} data-testid="pool-add-line"><Plus className="w-4 h-4 mr-1" />Add SKU</Button>
      </div>
      <Button onClick={submit} data-testid="pool-submit" className="bg-sky-600 hover:bg-sky-700">Create Work Pool</Button>
    </div>
  );
}

function CreateSingle() {
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({ task_type_code: "", priority: "normal" });
  useEffect(() => { api.get("/task-types", { params: { active_only: true } }).then((r) => setTypes((r.data || []).filter((t) => !t.allows_multi_sku))).catch(() => {}); }, []);
  const submit = async () => {
    try { await api.post("/tasks/manual", { ...f, expected_qty: f.expected_qty ? Number(f.expected_qty) : null }); toast.success("Task created"); setF({ task_type_code: "", priority: "normal" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 max-w-xl" data-testid="create-single-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Task Type</Label>
          <Select value={f.task_type_code} onValueChange={(v) => setF((p) => ({ ...p, task_type_code: v }))}><SelectTrigger data-testid="single-type"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{types.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs">Priority</Label>
          <Select value={f.priority} onValueChange={(v) => setF((p) => ({ ...p, priority: v }))}><SelectTrigger data-testid="single-priority"><SelectValue /></SelectTrigger><SelectContent>{["normal", "high", "urgent", "order_blocking"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs">Assign Team (optional)</Label><Input data-testid="single-team" value={f.assigned_team || ""} onChange={(e) => setF((p) => ({ ...p, assigned_team: e.target.value }))} placeholder="Packing Team" /></div>
        <div><Label className="text-xs">or Employee (optional)</Label><Input data-testid="single-emp" value={f.assigned_employee || ""} onChange={(e) => setF((p) => ({ ...p, assigned_employee: e.target.value }))} placeholder="FAS003" /></div>
        <div><Label className="text-xs">SKU (optional)</Label><Input value={f.sku || ""} onChange={(e) => setF((p) => ({ ...p, sku: e.target.value }))} /></div>
        <div><Label className="text-xs">Qty (optional)</Label><Input type="number" value={f.expected_qty || ""} onChange={(e) => setF((p) => ({ ...p, expected_qty: e.target.value }))} /></div>
        <div><Label className="text-xs">Due date (optional)</Label><Input type="date" value={f.due_date || ""} onChange={(e) => setF((p) => ({ ...p, due_date: e.target.value }))} /></div>
      </div>
      <Textarea placeholder="Notes" value={f.notes || ""} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))} rows={2} data-testid="single-notes" />
      <Button onClick={submit} data-testid="single-submit" className="bg-sky-600 hover:bg-sky-700">Create Task</Button>
    </div>
  );
}

function TaskTypes({ canManage }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/task-types").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (t, field) => {
    try { await api.put(`/task-types/${t.id}`, { [field]: !t[field] }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" data-testid="admin-task-types">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="text-left p-2">Type</th><th className="p-2">Eligible Teams</th><th className="p-2">Multi-SKU</th><th className="p-2">Self-Create</th><th className="p-2">KPI</th><th className="p-2">Active</th></tr></thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} className="border-t" data-testid={`type-row-${t.code}`}>
              <td className="p-2"><div className="font-medium">{t.name}</div><div className="font-mono text-[10px] text-slate-400">{t.code}</div></td>
              <td className="p-2 text-xs">{(t.eligible_teams || []).join(", ")}</td>
              <td className="p-2 text-center">{t.allows_multi_sku ? "✓" : "—"}</td>
              <td className="p-2 text-center">{t.allows_self_create ? "✓" : "—"}</td>
              <td className="p-2 text-center">{t.kpi_eligible ? "✓" : "—"}</td>
              <td className="p-2 text-center"><Switch checked={t.active} disabled={!canManage} onCheckedChange={() => toggle(t, "active")} data-testid={`type-active-${t.code}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Locations({ canManage }) {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/locations").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (f) => {
    try { await api.post("/locations", { ...f, physical_sequence: f.physical_sequence ? Number(f.physical_sequence) : null, pick_route: f.pick_route ? Number(f.pick_route) : null }); toast.success("Location added"); setDlg(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/locations/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div data-testid="admin-locations">
      {canManage && <Button size="sm" className="mb-3" data-testid="loc-add-btn" onClick={() => setDlg({})}><Plus className="w-4 h-4 mr-1" />Add Location</Button>}
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="text-left p-2">Rack</th><th className="p-2">Floor</th><th className="p-2">Type</th><th className="p-2">Seq</th><th className="p-2">Route</th><th className="p-2">Active</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-slate-400">No locations yet</td></tr>
              : rows.map((l) => (
                <tr key={l.id} className="border-t" data-testid={`loc-row-${l.rack_code}`}>
                  <td className="p-2 font-mono">{l.rack_code}</td><td className="p-2 text-center">{l.floor || "—"}</td>
                  <td className="p-2 text-center text-xs">{LOCATION_TYPES.find((x) => x.value === l.location_type)?.label}</td>
                  <td className="p-2 text-center">{l.physical_sequence ?? "—"}</td><td className="p-2 text-center">{l.pick_route ?? "—"}</td>
                  <td className="p-2 text-center">{l.active ? "✓" : "—"}</td>
                  <td className="p-2 text-right">{canManage && <Button size="icon" variant="ghost" onClick={() => del(l.id)}><Trash2 className="w-4 h-4" /></Button>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {dlg && <LocationDialog onClose={() => setDlg(null)} onSave={save} />}
    </div>
  );
}

function LocationDialog({ onClose, onSave }) {
  const [f, setF] = useState({ location_type: "picking_rack", active: true });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="loc-dialog">
        <DialogHeader><DialogTitle>Add Warehouse Location</DialogTitle><DialogDescription>Classification only — no inventory movement.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Rack Code</Label><Input data-testid="loc-rack" value={f.rack_code || ""} onChange={(e) => set("rack_code", e.target.value)} /></div>
          <div><Label className="text-xs">Floor</Label><Input data-testid="loc-floor" value={f.floor || ""} onChange={(e) => set("floor", e.target.value)} placeholder="1 / 2 / 3" /></div>
          <div><Label className="text-xs">Type</Label>
            <Select value={f.location_type} onValueChange={(v) => set("location_type", v)}><SelectTrigger data-testid="loc-type"><SelectValue /></SelectTrigger><SelectContent>{LOCATION_TYPES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Physical Seq</Label><Input type="number" data-testid="loc-seq" value={f.physical_sequence || ""} onChange={(e) => set("physical_sequence", e.target.value)} /></div>
            <div><Label className="text-xs">Pick Route</Label><Input type="number" data-testid="loc-route" value={f.pick_route || ""} onChange={(e) => set("pick_route", e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="loc-save" onClick={() => onSave(f)}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssuesPanel() {
  const [issues, setIssues] = useState([]);
  const [disc, setDisc] = useState([]);
  const load = () => { api.get("/ops/issues").then((r) => setIssues(r.data)).catch(() => {}); api.get("/ops/discrepancies").then((r) => setDisc(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const resolveIssue = async (id) => { try { await api.post(`/ops/issues/${id}/resolve`, { resolution_note: "Resolved" }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const resolveDisc = async (id) => { try { await api.post(`/ops/discrepancies/${id}/resolve`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="admin-issues">
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-3 py-2 border-b text-sm font-semibold">Order Issues</div>
        {issues.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">None</div>
          : issues.map((i) => (
            <div key={i.id} className="px-3 py-2 border-b last:border-0 text-sm flex justify-between items-start gap-2" data-testid={`issue-${i.id}`}>
              <div><div className="font-medium">{i.category}</div><div className="text-xs text-slate-500">{i.note}</div></div>
              {i.status !== "resolved" ? <Button size="sm" variant="outline" onClick={() => resolveIssue(i.id)} data-testid={`issue-resolve-${i.id}`}>Resolve</Button> : <Badge variant="outline" className="text-emerald-700 border-emerald-300">Resolved</Badge>}
            </div>
          ))}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-3 py-2 border-b text-sm font-semibold">Rack Discrepancies</div>
        {disc.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">None</div>
          : disc.map((d) => (
            <div key={d.id} className="px-3 py-2 border-b last:border-0 text-sm flex justify-between items-start gap-2" data-testid={`disc-${d.id}`}>
              <div><div className="font-medium font-mono">{d.sku} @ {d.rack}</div><div className="text-xs text-slate-500">{d.note}</div></div>
              {d.status !== "resolved" ? <Button size="sm" variant="outline" onClick={() => resolveDisc(d.id)} data-testid={`disc-resolve-${d.id}`}>Resolve</Button> : <Badge variant="outline" className="text-emerald-700 border-emerald-300">Resolved</Badge>}
            </div>
          ))}
      </div>
    </div>
  );
}
